import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import { trackEvent } from "../../../analytics";

const DiscoveryContext = createContext();

export const DiscoveryProvider = ({ children, token }) => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const fetchPreferences = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/user/discovery-preferences`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPreferences(response.data.discoveryPreferences);
    } catch (error) {
      console.error("Error fetching discovery preferences:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Force open wizard if in progress (steps 1, 2, or 3)
  useEffect(() => {
    if (
      preferences &&
      preferences.discoveryStep > 0 &&
      preferences.discoveryStep < 4
    ) {
      setWizardOpen(true);
    }
  }, [preferences?.discoveryStep]);

  const updatePreferences = async (updates) => {
    if (!token) return;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/user/discovery-preferences`,
        updates,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPreferences(response.data.discoveryPreferences);

      // Track specific events based on updates
      if (updates.discoveryStep !== undefined) {
        trackEvent("discovery_step_updated", { step: updates.discoveryStep });
        if (updates.discoveryStep === 4) trackEvent("discovery_flow_completed");
      }
      if (updates.hasSeenDiscoveryPrompt) trackEvent("discovery_prompt_shown");
      if (updates.dismissedHint)
        trackEvent("discovery_hint_dismissed", {
          hintId: updates.dismissedHint,
        });
      if (updates.featureUsed)
        trackEvent("feature_discovered", { feature: updates.featureUsed });
    } catch (error) {
      console.error("Error updating discovery preferences:", error);
    }
  };

  const markHintAsDismissed = (hintId) => {
    updatePreferences({ dismissedHint: hintId });
  };

  const markSpotlightAsDismissed = (spotlightId) => {
    updatePreferences({ dismissedSpotlight: spotlightId });
  };

  const markAnnouncementAsSeen = (announcementId) => {
    updatePreferences({ announcementSeen: announcementId });
  };

  const recordFeatureUsage = (featureKey) => {
    updatePreferences({ featureUsed: featureKey });
  };

  const setDiscoveryStep = (step) => {
    updatePreferences({ discoveryStep: step });
  };

  const markPromptAsSeen = () => {
    updatePreferences({ hasSeenDiscoveryPrompt: true });
  };

  return (
    <DiscoveryContext.Provider
      value={{
        preferences,
        loading,
        updatePreferences,
        markHintAsDismissed,
        markSpotlightAsDismissed,
        markAnnouncementAsSeen,
        recordFeatureUsage,
        setDiscoveryStep,
        markPromptAsSeen,
        wizardOpen,
        setWizardOpen,
        refreshPreferences: fetchPreferences,
      }}
    >
      {children}
    </DiscoveryContext.Provider>
  );
};

export const useDiscovery = () => {
  const context = useContext(DiscoveryContext);
  if (!context) {
    throw new Error("useDiscovery must be used within a DiscoveryProvider");
  }
  return context;
};
